import * as THREE from 'three';
import { hexToRgb } from './impactFxMath';

type Vec3 = [number, number, number];

function pushTri(
  positions: number[],
  normals: number[],
  colors: number[],
  a: Vec3,
  b: Vec3,
  c: Vec3,
  rgb: { r: number; g: number; b: number },
): void {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const abz = b[2] - a[2];
  const acx = c[0] - a[0];
  const acy = c[1] - a[1];
  const acz = c[2] - a[2];
  let nx = aby * acz - abz * acy;
  let ny = abz * acx - abx * acz;
  let nz = abx * acy - aby * acx;
  const len = Math.hypot(nx, ny, nz) || 1;
  nx /= len;
  ny /= len;
  nz /= len;
  for (const v of [a, b, c]) {
    positions.push(v[0], v[1], v[2]);
    normals.push(nx, ny, nz);
    colors.push(rgb.r, rgb.g, rgb.b);
  }
}

function finishGeometry(positions: number[], normals: number[], colors: number[]): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  return geometry;
}

/** Unique corners after welding — used by tests to keep shards in the 8–14 band. */
export function uniqueCornerCount(positions: number[], eps = 1e-4): number {
  const keys = new Set<string>();
  for (let i = 0; i < positions.length; i += 3) {
    const x = Math.round(positions[i] / eps);
    const y = Math.round(positions[i + 1] / eps);
    const z = Math.round(positions[i + 2] / eps);
    keys.add(`${x},${y},${z}`);
  }
  return keys.size;
}

/**
 * Angular masonry shard. Variant 0 = sheared brick, 1 = chamfered wedge, 2 = flatter flake.
 * +Z / +X faces carry the aged outer skin; broken faces carry the fresh interior.
 */
export function createMasonryShardGeometry(
  variant: 0 | 1 | 2,
  outerHex: string,
  interiorHex: string,
): THREE.BufferGeometry {
  const outer = hexToRgb(outerHex);
  const interior = hexToRgb(interiorHex);
  const corners: Vec3[] = variant === 0
    ? [
        [-0.50, -0.34, -0.42],
        [0.54, -0.28, -0.38],
        [0.38, 0.42, -0.46],
        [-0.40, 0.46, -0.28],
        [-0.46, -0.38, 0.48],
        [0.50, -0.20, 0.36],
        [0.32, 0.48, 0.44],
        [-0.52, 0.28, 0.34],
        [0.08, 0.22, 0.58],
      ]
    : variant === 1
      ? [
          [-0.46, -0.40, -0.36],
          [0.58, -0.22, -0.48],
          [0.22, 0.50, -0.30],
          [-0.52, 0.34, -0.50],
          [-0.38, -0.46, 0.42],
          [0.44, -0.16, 0.52],
          [0.16, 0.38, 0.40],
          [-0.56, 0.18, 0.28],
          [0.62, 0.12, 0.04],
        ]
      : [
          [-0.56, -0.22, -0.48],
          [0.52, -0.18, -0.40],
          [0.44, 0.26, -0.52],
          [-0.40, 0.30, -0.34],
          [-0.50, -0.24, 0.46],
          [0.48, -0.14, 0.38],
          [0.36, 0.28, 0.50],
          [-0.46, 0.20, 0.32],
          [0.04, 0.36, 0.12],
        ];

  const faces: Array<[number, number, number, boolean]> = [
    [4, 5, 8, true],
    [5, 6, 8, true],
    [6, 7, 8, true],
    [7, 4, 8, true],
    [1, 0, 3, false],
    [1, 3, 2, false],
    [5, 1, 2, true],
    [5, 2, 6, true],
    [0, 4, 7, false],
    [0, 7, 3, false],
    [7, 6, 2, false],
    [7, 2, 3, false],
    [0, 1, 5, false],
    [0, 5, 4, false],
  ];

  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  for (const [ia, ib, ic, skin] of faces) {
    pushTri(positions, normals, colors, corners[ia], corners[ib], corners[ic], skin ? outer : interior);
  }
  return finishGeometry(positions, normals, colors);
}

/**
 * Elongated wood splinter. Long faces are bark (outer); broken ends are fresh sapwood.
 */
export function createSplinterGeometry(
  variant: 0 | 1 | 2,
  outerHex: string,
  interiorHex: string,
): THREE.BufferGeometry {
  const outer = hexToRgb(outerHex);
  const interior = hexToRgb(interiorHex);
  const kink = variant === 0 ? 0 : variant === 1 ? 0.06 : -0.05;
  const taper = variant === 2 ? 0.72 : 1;
  const corners: Vec3[] = [
    [-0.13, -0.08, -0.52],
    [0.12, -0.07 + kink, -0.50],
    [0.10 * taper, 0.09, -0.48],
    [-0.12, 0.08, -0.51],
    [-0.10, -0.07, 0.52],
    [0.11, -0.06 + kink * 0.5, 0.50],
    [0.09 * taper, 0.08, 0.48],
    [-0.11, 0.07, 0.51],
  ];

  const faces: Array<[number, number, number, boolean]> = [
    [1, 0, 3, false],
    [1, 3, 2, false],
    [4, 5, 6, false],
    [4, 6, 7, false],
    [5, 1, 2, true],
    [5, 2, 6, true],
    [0, 4, 7, true],
    [0, 7, 3, true],
    [3, 7, 6, true],
    [3, 6, 2, true],
    [0, 1, 5, true],
    [0, 5, 4, true],
  ];

  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  for (const [ia, ib, ic, skin] of faces) {
    pushTri(positions, normals, colors, corners[ia], corners[ib], corners[ic], skin ? outer : interior);
  }
  return finishGeometry(positions, normals, colors);
}
